import * as R from 'ramda';

import moment from 'moment';
import {createAction} from 'redux-actions';

import history from 'js/creator/history';
import {showNotification} from 'js/creator/actions';
import {track} from 'js/common/analytics';
import typeformTemplates from 'js/creator/common/typeform_templates';

import * as DashboardApiClient from 'js/creator/dashboard/dashboard_api_client';
import * as OnboardingApiClient from 'js/creator/onboarding/onboarding_api_client';

const {InviteStatus} = window.sn.enums;

// action types
const FETCH_CAMPAIGN_INVITES = 'FETCH_CAMPAIGN_INVITES';
const FETCH_CAMPAIGN_INVITE = 'FETCH_CAMPAIGN_INVITE';
const UPDATE_CAMPAIGN_INVITE = 'UPDATE_CAMPAIGN_INVITE';
const UPDATE_CURRENT_INVITE_STATE = 'UPDATE_CURRENT_INVITE_STATE';
const WIPE_CAMPAIGN_INVITES = 'WIPE_CAMPAIGN_INVITES';
const GRANT_EXTENSION = 'GRANT_EXTENSION';

// action creators
export const fetchCampaignInvites = createAction(
    FETCH_CAMPAIGN_INVITES,
    DashboardApiClient.getCampaignInvites
);

export const fetchCampaignInvite = createAction(
    FETCH_CAMPAIGN_INVITE,
    OnboardingApiClient.getCampaignInvite
);

export const updateCampaignInvite = createAction(
    UPDATE_CAMPAIGN_INVITE,
    OnboardingApiClient.updateCampaignInvite
);

export const updateCurrentInviteState = createAction(UPDATE_CURRENT_INVITE_STATE, R.identity);

export const wipeCampaignInvites = createAction(WIPE_CAMPAIGN_INVITES, R.identity);

export const grantExtension = createAction(GRANT_EXTENSION, DashboardApiClient.applyForExtension);

export function doGrantExtension(inviteId) {
    return (dispatch, getState) => {
        dispatch(grantExtension(inviteId)).then(response => {
            if (R.not(R.has('error', response))) {
                const {
                    campaignInvite: {campaign, brand},
                    userEmail
                } = getState();

                track(userEmail, 'Pub dash guidelines clicked campaign extension', {
                    'Campaign Funnel ID': campaign.id,
                    'Campaign Funnel Name': campaign.name,
                    'Brand Name': brand.name,
                    'Extended flight end date for Campaign': campaign.name
                });

                dispatch(
                    showNotification({
                        type: 'success',
                        message: 'Your extension has been granted.'
                    })
                );
            }
        });
    };
}

export function fetchCampaignInviteIfNeeded(inviteId) {
    return (dispatch, getState) => {
        const state = getState();

        if (!state.campaignInvite || state.campaignInvite.id !== inviteId) {
            return dispatch(fetchCampaignInvite(inviteId));
        }

        return Promise.resolve();
    };
}

export function fetchCampaignInviteOnUpdate(inviteId) {
    return dispatch => {
        dispatch(wipeCampaignInvites());
        dispatch(fetchCampaignInvite(inviteId));
    };
}

export function doWipeCampaignInvites() {
    return dispatch => dispatch(wipeCampaignInvites());
}

export function doUpdateCurrentInviteState(nextInviteState) {
    return dispatch => {
        dispatch(updateCurrentInviteState(nextInviteState));
    };
}

export function fetchCampaignInvitesOnUpdate() {
    return dispatch => dispatch(fetchCampaignInvites());
}

export function fetchCampaignInvitesIfNeeded() {
    return (dispatch, getState) => {
        const state = getState();

        if (!(state.campaignInvites && state.campaignInvites.length)) {
            return dispatch(fetchCampaignInvites());
        }

        return Promise.resolve();
    };
}

export function updateCampaignInviteAndShowNext(invite) {
    return (dispatch, getState) =>
        dispatch(updateCampaignInvite(invite)).then(() => {
            const {
                campaignInvite: {status, id, campaign, brand, is_paid, payout_amount},
                userEmail,
                user
            } = getState();
            const {prevStatus} = invite;
            const wasPending = prevStatus === InviteStatus.PENDING;
            const wasAccepted = prevStatus === InviteStatus.ACCEPTED;
            const wasApproved = prevStatus === InviteStatus.APPROVED;
            const nowAccepted = status === InviteStatus.ACCEPTED;
            const nowDenied = status === InviteStatus.DENIED;
            const profileProperties = R.pick(
                [
                    'address',
                    'age',
                    'city',
                    'country',
                    'name',
                    'phone_number',
                    'state',
                    'zip_code',
                    'paypal_account'
                ],
                R.propOr({}, 'profile', user)
            );

            const properties = {
                'Campaign Funnel ID': campaign.id,
                'Campaign Funnel Name': campaign.name,
                'Brand Name': brand.name,
                Payout: payout_amount,
                is_paid: is_paid ? 1 : 0,
                ...profileProperties
            };

            if (wasPending && nowAccepted) {
                track(
                    userEmail,
                    'Pub dash guidelines clicked invite accept',
                    R.merge(properties, {
                        'Accepted Campaign': campaign.name
                    })
                );

                history.pushState(null, `/creator/onboarding/invite/${invite.id}/whats_next`);
            } else if (wasPending && nowDenied) {
                track(
                    userEmail,
                    'Pub dash guidelines clicked invite deny',
                    R.merge(properties, {
                        'Denied Campaign': campaign.name
                    })
                );
                history.pushState(null, `/creator/onboarding/invite/${invite.id}/guidelines`);
            } else if ((wasAccepted && nowDenied) || (wasApproved && nowDenied)) {
                track(
                    userEmail,
                    'Pub dash guidelines clicked creator opt out',
                    R.merge(properties, {
                        'Opted Out Campaign': campaign.name
                    })
                );

                window.location =
                    `${typeformTemplates.optOutSurvey}` +
                    `?campaignname=${campaign.name}` +
                    `&email=${userEmail}` +
                    `&brandname=${brand.name}` +
                    `&leadid=${user.lead_id}` +
                    `&inviteid=${id}` +
                    `&payoutamount=${payout_amount}` +
                    `&postingwindowstart=${moment(campaign.start.date).format('MM/DD/YY')}` +
                    `&postingwindowend=${moment(campaign.end.date).format('MM/DD/YY')}`;
            } else {
                history.pushState(null, `/creator/onboarding/invite/${invite.id}/whats_next`);
            }
        });
}

// reducers
export default {
    [FETCH_CAMPAIGN_INVITES]: (state, action) => R.assoc('campaignInvites', action.payload, state),

    [FETCH_CAMPAIGN_INVITE]: (state, action) => R.assoc('campaignInvite', action.payload, state),

    [UPDATE_CAMPAIGN_INVITE]: (state, action) => R.assoc('campaignInvite', action.payload, state),

    [UPDATE_CURRENT_INVITE_STATE]: (state, action) =>
        R.assoc('campaignInvite', R.merge(state.campaignInvite, action.payload), state),

    [WIPE_CAMPAIGN_INVITES]: state => R.dissoc('campaignInvites', state),

    [GRANT_EXTENSION]: (state, action) =>
        R.assoc('campaignInvite', R.merge(state.campaignInvite, action.payload.invite), state)
};



// WEBPACK FOOTER //
// ./creator/ducks/invites.js